/* Test Bundle Builder (collection.test-bundle): product selection, "My Bundle" slots, AJAX tabs and add-to-cart. */

if (!customElements.get('test-bundle')) {
  customElements.define('test-bundle', class TestBundle extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      this.min = parseInt(d.min, 10) || 1;
      this.max = parseInt(d.max, 10) || 0;
      this.baseSlots = parseInt(d.slots, 10) || 3;
      this.money = new Intl.NumberFormat(d.locale || 'en', { style: 'currency', currency: d.currency || 'USD' });
      this.selection = {}; // productId -> qty
      this.order = []; // productIds in the order they were added (fills slots in order)
      this.items = {}; // productId -> product details, kept so selections survive page changes
      this.slotMessages = {};
      (d.slotMessages || '').split('||').forEach((pair) => {
        const [qty, msg] = pair.split('::');
        if (qty && msg) this.slotMessages[parseInt(qty, 10)] = msg;
      });

      this.slots = this.querySelector('[data-tb-slots]');
      this.submit = this.querySelector('[data-tb-submit]');
      this.submitLabel = this.querySelector('[data-tb-submit-label]');
      this.submitPrice = this.querySelector('[data-tb-submit-price]');
      this.error = this.querySelector('[data-tb-error]');
      this.tray = this.querySelector('[data-tb-tray]');
      this.toggle = this.querySelector('[data-tb-toggle]');
      this.progress = this.querySelector('[data-tb-progress]');

      this.indexCards();
      this.addEventListener('click', (e) => this.onClick(e));
      this.restore();
      this.render();
    }

    indexCards() {
      this.cards = {};
      this.querySelectorAll('[data-tb-product]').forEach((card) => {
        this.cards[card.dataset.tbProduct] = card;
      });
    }

    /* ---------- Events ---------- */

    onClick(e) {
      const tab = e.target.closest('[data-tb-tab]');
      if (tab) {
        e.preventDefault();
        this.setActiveTab(tab);
        return this.loadPage(tab.href);
      }

      const card = e.target.closest('[data-tb-product]');
      if (card) {
        const id = card.dataset.tbProduct;
        if (e.target.closest('[data-tb-add], [data-tb-plus]')) return this.change(id, 1);
        if (e.target.closest('[data-tb-minus]')) return this.change(id, -1);
      }

      const remove = e.target.closest('[data-tb-remove]');
      if (remove) return this.change(remove.dataset.tbRemove, -1);

      if (e.target.closest('[data-tb-toggle]')) return this.toggleTray();
      if (e.target.closest('[data-tb-submit]')) return this.addToCart();
    }

    // Tabs are tag-filtered views of the collection (/collections/<handle>/<tag>), loaded via AJAX
    setActiveTab(tab) {
      this.querySelectorAll('[data-tb-tab]').forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        if (active) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    toggleTray(force) {
      const open = typeof force === 'boolean' ? force : !this.tray.classList.contains('is-open');
      this.tray.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.toggle.setAttribute('aria-label', open ? this.dataset.labelHide : this.dataset.labelView);
    }

    /* ---------- AJAX tab loading (Section Rendering API) ---------- */

    async loadPage(href) {
      const products = this.querySelector('[data-tb-products]');
      if (!products || products.classList.contains('is-loading')) return;

      const pageUrl = new URL(href, window.location.href);
      const fetchUrl = new URL(pageUrl);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

      products.classList.add('is-loading');
      products.setAttribute('aria-busy', 'true');

      try {
        const html = await (await fetch(fetchUrl.toString())).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-tb-products]');
        if (!next) throw new Error('No products returned');

        products.innerHTML = next.innerHTML;
        history.replaceState(history.state, '', pageUrl.pathname + pageUrl.search);

        this.indexCards();
        this.render();
      } catch (err) {
        // Fall back to a normal page load
        window.location.href = pageUrl.toString();
      } finally {
        products.classList.remove('is-loading');
        products.removeAttribute('aria-busy');
      }
    }

    /* ---------- State ---------- */

    total() {
      return Object.values(this.selection).reduce((sum, qty) => sum + qty, 0);
    }

    snapshot(card) {
      const d = card.dataset;
      return {
        variantId: d.variantId,
        title: d.title,
        price: parseInt(d.price, 10) || 0,
        compare: parseInt(d.compare, 10) || 0,
        image: d.image || '',
        max: parseInt(d.max, 10) || 0
      };
    }

    change(id, delta) {
      const card = this.cards[id];
      if (card) this.items[id] = this.snapshot(card); // refresh details while the card is on screen
      const item = this.items[id];
      if (!item) return;

      const current = this.selection[id] || 0;
      let next = delta === -Infinity ? 0 : current + delta;

      if (delta > 0) {
        if (this.max && this.total() + delta > this.max) return this.flash(`Your bundle is full (${this.max} items). Remove an item to add another.`);
        if (next > item.max) return this.flash(item.max > 1 ? `You can add up to ${item.max} of ${item.title}.` : `${item.title} is already in your bundle.`);
      }

      next = Math.max(0, next);
      if (next === 0) {
        delete this.selection[id];
        delete this.items[id];
        this.order = this.order.filter((x) => x !== id);
      } else {
        this.selection[id] = next;
        if (!this.order.includes(id)) this.order.push(id);
      }
      this.hideError();
      this.render();
    }

    save() {
      try {
        localStorage.setItem(this.dataset.storageKey, JSON.stringify({ selection: this.selection, order: this.order, items: this.items }));
      } catch (e) { /* storage unavailable */ }
    }

    restore() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '{}');
        const savedItems = saved.items || {};
        (saved.order || []).forEach((id) => {
          const card = this.cards[id];
          const item = card ? this.snapshot(card) : savedItems[id];
          if (!item || !item.variantId) return;
          let qty = Math.min(parseInt((saved.selection || {})[id], 10) || 0, item.max);
          if (this.max) qty = Math.min(qty, this.max - this.total());
          if (qty > 0) {
            this.items[id] = item;
            this.selection[id] = qty;
            this.order.push(id);
          }
        });
      } catch (e) { /* storage unavailable */ }
    }

    /* ---------- Render ---------- */

    render() {
      const count = this.total();
      const full = this.max && count >= this.max;
      let subtotal = 0;
      let compareTotal = 0;
      const units = [];

      // Product cards on the current page
      Object.entries(this.cards).forEach(([id, card]) => {
        const qty = this.selection[id] || 0;
        const add = card.querySelector('[data-tb-add]');
        const stepper = card.querySelector('[data-tb-stepper]');
        const stock = parseInt(card.dataset.max, 10) || 0;
        card.classList.toggle('is-selected', qty > 0);

        // Once added, the "Add To Bundle" button is replaced by a quantity stepper
        if (add && !add.disabled && stepper) {
          add.hidden = qty > 0;
          stepper.hidden = qty === 0;
          stepper.querySelector('[data-tb-qty]').textContent = qty;
          stepper.querySelector('[data-tb-plus]').disabled = qty >= stock || full;
          add.classList.toggle('is-blocked', full);
        }
      });

      // Slots, filled in the order products were added (from any page)
      this.order.forEach((id) => {
        const item = this.items[id];
        const qty = this.selection[id] || 0;
        if (!item) return;
        subtotal += item.price * qty;
        compareTotal += Math.max(item.price, item.compare) * qty;
        for (let i = 0; i < qty; i++) units.push({ id, item });
      });

      const slotTotal = this.max ? this.max : Math.max(this.baseSlots, units.length + 1);
      const select = this.escape(this.dataset.labelSelect);
      let html = '';
      for (let i = 0; i < slotTotal; i++) {
        const unit = units[i];
        if (!unit) {
          const hint = this.slotMessages[i + 1] ? `<span class="tb-slot__hint">${this.escape(this.slotMessages[i + 1])}</span>` : '';
          html += `<li class="tb-slot"><span class="tb-slot__box" aria-hidden="true">+</span><span class="tb-slot__info"><span class="tb-slot__label">${select}</span>${hint}</span></li>`;
          continue;
        }
        const title = this.escape(unit.item.title);
        const img = this.escape(unit.item.image);
        html += `
          <li class="tb-slot is-filled">
            <span class="tb-slot__box">${img ? `<img src="${img}" alt="" width="72" height="72" loading="lazy">` : ''}</span>
            <span class="tb-slot__info">
              <span class="tb-slot__title">${title}</span>
              <span class="tb-slot__price">
                ${unit.item.compare > unit.item.price ? `<s>${this.format(unit.item.compare)}</s>` : ''}
                <span>${this.format(unit.item.price)}</span>
              </span>
            </span>
            <button type="button" class="tb-slot__remove" data-tb-remove="${this.escape(unit.id)}" aria-label="Remove ${title}">&times;</button>
          </li>`;
      }
      this.slots.innerHTML = html;

      // Counts, savings and the price shown inside the add-to-cart button
      this.querySelector('[data-tb-count]').textContent = count;
      const save = this.querySelector('[data-tb-save]');
      const saving = count > 0 && compareTotal > subtotal;
      save.hidden = !saving;
      save.innerHTML = saving ? `<strong>Total Savings:</strong> ${this.format(compareTotal - subtotal)}` : '';
      this.submitPrice.innerHTML = count > 0
        ? `${this.format(subtotal)}${compareTotal > subtotal ? ` <s>${this.format(compareTotal)}</s>` : ''}`
        : '';

      // Progress milestones
      if (this.progress) {
        const end = parseInt(this.progress.dataset.tbProgressMax, 10) || 1;
        this.querySelector('[data-tb-progress-fill]').style.width = `${Math.min(100, (count / end) * 100)}%`;
        this.progress.querySelectorAll('[data-tb-milestone]').forEach((m) => {
          m.classList.toggle('is-reached', count >= parseInt(m.dataset.tbMilestone, 10));
        });
      }

      if (this.submit.getAttribute('aria-busy') !== 'true') {
        this.submit.disabled = count < this.min;
      }
      this.save();
    }

    escape(value) {
      return String(value || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    format(cents) {
      return this.money.format(cents / 100);
    }

    flash(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => this.hideError(), 4000);
    }

    hideError() {
      this.error.hidden = true;
    }

    /* ---------- Cart ---------- */

    async addToCart() {
      if (this.total() < this.min || this.submit.getAttribute('aria-busy') === 'true') return;

      const bundleId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const items = this.order.filter((id) => this.items[id]).map((id) => ({
        id: parseInt(this.items[id].variantId, 10),
        quantity: this.selection[id],
        properties: {
          _bundle_builder: this.dataset.bundleName || 'Bundle Builder',
          _bundle_id: bundleId
        }
      }));

      this.submit.disabled = true;
      this.submit.setAttribute('aria-busy', 'true');
      this.submitLabel.textContent = this.dataset.labelAdding;
      this.submitPrice.hidden = true;
      this.hideError();

      try {
        const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add your bundle to the cart.');

        this.submitLabel.textContent = this.dataset.labelAdded;
        this.submitPrice.hidden = false;
        this.selection = {};
        this.order = [];
        this.items = {};
        this.submit.removeAttribute('aria-busy');
        this.render();
        setTimeout(() => { this.submitLabel.textContent = this.dataset.labelSubmit; }, 2000);
        this.afterAdd();
      } catch (err) {
        this.submit.removeAttribute('aria-busy');
        this.submitLabel.textContent = this.dataset.labelSubmit;
        this.submitPrice.hidden = false;
        this.submit.disabled = this.total() < this.min;
        this.flash(err.message);
      }
    }

    async afterAdd() {
      const drawer = document.querySelector('cart-drawer');
      const usePage = this.dataset.afterAdd === 'cart' || !drawer || (window.theme && theme.settings && theme.settings.cartType === 'page');
      if (usePage) {
        window.location.href = this.dataset.cartUrl;
        return;
      }

      this.toggleTray(false);
      try {
        const cart = await (await fetch(`${this.dataset.cartUrl}.js`)).json();
        if (window.theme && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'test-bundle', cart });
        }
      } catch (e) { /* cart count refresh is best-effort */ }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    }
  });
}